import translate from '@translate';
import React, { FC, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import FormBlock from 'src/components/FormBlock';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ProductCompletionSheetType } from 'src/types/productCompletionSheet';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import {
    getProductCompletionSheetDetailFetch,
    createProductCompletionSheetDetailFetch,
    updateProductCompletionSheetDetailFetch,
    clearProductCompletionSheetDetail,
} from 'src/resources/productCompletionSheetDetail/reducer';
import { useCheckPermission } from 'src/utils/checkPermission';
import Detail from './Detail';
import { useNavigate } from 'react-router-dom';
import ProductsTable from './ProductsTable';
import TopBlock from './TopBlock';
import getBackUrl from 'src/utils/getBackUrl';
import { EditKeysType, EDIT_KEYS, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import NewProductBlock from './NewProductBlock';
import { getProductsListFetch } from 'src/resources/productsList/reducer';
import { getBillsOfMaterialsListFetch } from 'src/resources/billsOfMaterialsList/reducer';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { sizes } from 'src/themes/default';
import isDeepEqual from 'src/utils/isDeepEqual';
import { setStorageData } from 'src/resources/storage/reducer';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';

const initData = { PRODUCT_COMPLETION_FINAL_PRODUCTS: [] };
const ProductCompletionSheetDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { idProductCompletionSheet } = useParams();

    const newProductCompletionSheet = !idProductCompletionSheet;

    const formRef = useRef<HTMLFormElement>(null);
    const productsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.productCompletionSheetDetail.data);
    const productsList = useSelector((state: RootStoreType) => state.productsList.data);
    const billsOfMaterialList = useSelector((state: RootStoreType) => state.billsOfMaterialList.data);
    const loading = useSelector((state: RootStoreType) => state.productCompletionSheetDetail.loading);
    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);
    const productCompletionSheetStatusCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.productCompletionSheetStatusCodes
    );
    const [productsConstatns, setProductsConstatns] = useState<ConstantsCodeType[]>([]);
    const [billsOfMaterialConstatns, setBillsOfMaterialConstatns] = useState<ConstantsCodeType[]>([]);
    const [billsOfMaterialNameConstatns, setBillsOfMaterialNameConstatns] = useState<ConstantsCodeType[]>([]);

    const [editData, setEditData] = useState<ProductCompletionSheetType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const disableEdit = newProductCompletionSheet
        ? !useCheckPermission('PRODUCT_COMPLETION_SHEET.CREATE')
        : !useCheckPermission('PRODUCT_COMPLETION_SHEET.UPDATE');

    const backUrl = getBackUrl(
        locationHistory,
        '/commands/product_completion_sheets/',
        '/commands/product_completion_sheets/new/'
    );

    useEffect(() => {
        if (idProductCompletionSheet) {
            dispatch(
                setLocationParams({
                    name: translate('productCompletionSheetDetail.title', { idProductCompletionSheet }),
                    backUrl,
                })
            );
        } else {
            dispatch(setLocationParams({ name: translate('productCompletionSheetDetail.titleNew'), backUrl }));
        }
    }, [idProductCompletionSheet, backUrl, locationHistory]);

    useEffect(() => {
        if (idProductCompletionSheet) {
            dispatch(getProductCompletionSheetDetailFetch(idProductCompletionSheet));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearProductCompletionSheetDetail());
        };
    }, [idProductCompletionSheet]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idProductCompletionSheet && data?.ID) {
            navigate(`/commands/product_completion_sheets/${data.ID}`);
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        if (!productsList) {
            dispatch(getProductsListFetch({}));
        } else {
            setProductsConstatns(
                productsList?.map((item) => ({ label: `${item.SKU} | ${item.NAME}`, value: item.ID || '' })) || []
            );
        }
    }, [productsList]);

    useEffect(() => {
        if (!billsOfMaterialList) {
            dispatch(getBillsOfMaterialsListFetch(null));
        } else {
            setBillsOfMaterialConstatns(
                billsOfMaterialList
                    // ?.filter((item) => item.ACTIVE && item.FINAL_PRODUCT)
                    .map((item) => ({
                        label: `${item.NAME}`,
                        value: item.ID || '',
                    }))
            );

            setBillsOfMaterialNameConstatns(
                billsOfMaterialList
                    ?.filter((item) => item.ACTIVE && item.FINAL_PRODUCT)
                    .map((item) => ({
                        label: `${item.FINAL_PRODUCT?.SKU} | ${item.FINAL_PRODUCT?.NAME}`,
                        value: item.ID || '',
                        oreginalData: item,
                    }))
            );
        }
    }, [billsOfMaterialList]);

    // save edit data -start
    const editDataRef = useRef<ProductCompletionSheetType>({});
    useEffect(() => {
        if (
            newProductCompletionSheet &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/commands/product_completion_sheets') ||
                samePaths(locationHistory[1]?.pathname, '/commands/product_completion_sheets/new'))
        ) {
            setConfirmParams({
                title: translate('productCompletionSheetDetail.confirmGetNewDataStorage'),
                content: translate('productCompletionSheetDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                },
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: ProductCompletionSheetType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProduct: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (value.length) {
            delete newErrors.PRODUCT_COMPLETION_FINAL_PRODUCTS;
        } else {
            newErrors.PRODUCT_COMPLETION_FINAL_PRODUCTS = true;
        }
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(formRef);
        if (!editData?.PRODUCT_COMPLETION_FINAL_PRODUCTS?.length) {
            newFormErrors.PRODUCT_COMPLETION_FINAL_PRODUCTS = true;
        }

        if (!Object.keys(newFormErrors).length) {
            setConfirmParams({
                title: translate(
                    newProductCompletionSheet
                        ? 'productCompletionSheetDetail.create'
                        : 'productCompletionSheetDetail.save'
                ),
                content: translate(
                    newProductCompletionSheet
                        ? 'productCompletionSheetDetail.createConfirm'
                        : 'productCompletionSheetDetail.saveConfirm'
                ),
                action: () => {
                    const formatData = {
                        ...editData,
                        PRODUCT_COMPLETION_FINAL_PRODUCTS: editData?.PRODUCT_COMPLETION_FINAL_PRODUCTS?.map(
                            (product) => {
                                const formatProduct = { ...product };
                                if ((formatProduct.ID || -1) < 0) {
                                    delete formatProduct.ID;
                                }
                                return formatProduct;
                            }
                        ),
                    };
                    if (newProductCompletionSheet) {
                        dispatch(createProductCompletionSheetDetailFetch(formatData));
                    } else {
                        dispatch(
                            updateProductCompletionSheetDetailFetch({ idProductCompletionSheet, data: formatData })
                        );
                    }
                },
            });
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <TopBlock
                newProductCompletionSheet={newProductCompletionSheet}
                disableEdit={disableEdit}
                handleSave={handleSave}
            />
            {loading && <Loading />}

            <FormBlock sx={{ marginBottom: sizes.blocks_top_MG }}>
                <form ref={formRef}>
                    <Detail
                        onChange={onChange}
                        editData={editData}
                        editErrors={editErrors}
                        newProductCompletionSheet={newProductCompletionSheet}
                        disableEdit={disableEdit}
                        productCompletionSheetStatusCodes={productCompletionSheetStatusCodes}
                    />
                </form>
            </FormBlock>
            {!disableEdit && (
                <FormBlock>
                    <NewProductBlock
                        data={editData.PRODUCT_COMPLETION_FINAL_PRODUCTS || []}
                        onChange={(value: any) => onChange('PRODUCT_COMPLETION_FINAL_PRODUCTS', value, false)}
                        disableEdit={disableEdit}
                        billsOfMaterialNameConstatns={billsOfMaterialNameConstatns}
                        editErrors={editErrors}
                    />
                </FormBlock>
            )}
            <form ref={productsFormRef}>
                <ProductsTable
                    newProductCompletionSheet={newProductCompletionSheet}
                    data={editData.PRODUCT_COMPLETION_FINAL_PRODUCTS}
                    editErrors={editErrors}
                    onChange={(value: any, error: boolean, errorKey?: string) => {
                        onChangeProduct('PRODUCT_COMPLETION_FINAL_PRODUCTS', value, error, errorKey);
                    }}
                    disableEdit={disableEdit}
                    productsConstatns={productsConstatns}
                    billsOfMaterialConstatns={billsOfMaterialConstatns}
                />
            </form>
            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </>
    );
};

export default ProductCompletionSheetDetailContainer;
